import React, { forwardRef, ReactNode, useContext, useMemo } from 'react';
import { DOMProps } from '@react-types/shared';
import { getPlatformStyles, isMobile } from '@direct-frontend/platform-code';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { chain } from 'react-aria';

import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';
import { ListSize } from '../ListBoxBase/types';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { MenuListContext } from '../MenuTrigger/utils';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { VStack } from '../Stack';
import { useVStack } from '../../hooks/useStack';

import { ListContext, ListSearchContext } from './utils';
import { ListSection } from './ListSection';
import { ListItem } from './ListItem';
import { i18n } from './List.i18n';

getPlatformStyles({
    common: import('./List.css'),
    desktop: import('./List@desktop.css'),
});

export const cls = cn('List');

export interface ListProps extends TestProps, DOMProps, Loggable {
    /**
     * Размер списка, на мобилках — всегда m
     */
    size: ListSize;

    /**
     * Управление шириной списка
     * @default auto
     */
    width?: 'auto' | 'max' | number;

    /**
     * Элементы списка
     * @expectedNode `ListItem` | `ListSection` | `ListCustomItem` | `ListInputItem` |
     *               `ListStubItem` | `ListTreeItem` | `ListSubmenuItem`
     */
    children?: ReactNode;

    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Действие при нажатии на один из вложенных интерактивных элементов
     * Не срабатывает на нажатие задизейбленных элементов или элементов без ссылки и обработчика клика
     * @example onItemClick={closePopup}
     */
    onItemClick?: () => void;

    /**
     * Секция с элементами, которая будет запинена перед основным контентом списка.
     * Чаще всего используется для поиска, но принимает любые элементы в формате списка
     * @expectedNode `ListItem` | `ListSection` | `ListCustomItem` | `ListInputItem` |
     *               `ListStubItem` | `ListTreeItem` | `ListSubmenuItem`
     * @example pinnedSection={<ListInputItem placeholder="Поиск" />}
     */
    pinnedSection?: ReactNode;

    /**
     * Секция с элементами, которая будет запинена после основного контента списка.
     * Чаще всего используется для экшенов и переходов по ссылкам, но принимает любые элементы в формате списка
     * @expectedNode `ListItem` | `ListSection` | `ListCustomItem` | `ListInputItem` |
     *               `ListStubItem` | `ListTreeItem` | `ListSubmenuItem`
     * @example pinnedSectionAfter={<ListItem>Добавить все</ListItem>}
     */
    pinnedSectionAfter?: ReactNode;

    /**
     * Нужно ли отрисовывать разделитель между запиненными секциями и основным контентом списка.
     * По умолчанию разделитель показывается, если есть основной контент (children)
     * @deprecated Используется только внутри библиотеки
     */
    showSeparator?: boolean;

    /**
     * Максимальная высота списка
     * Рекомендуется передавать, если список может быть большим
     * @example maxHeight={400}
     */
    maxHeight?: number;

    /**
     * Отрисовываем ли в компоненте VirtualList
     *
     * @default false
     * @deprecated Используется только внутри библиотеки
     */
    isVirtualList?: boolean;

    /**
     * Поисковый запрос для фильтрации элементов списка
     */
    search?: string;

    /**
     * Текст пустого состояния при активном поиске
     * @default Ничего не найдено
     */
    searchEmptyText?: string;
}

/**
 * Компонент для отображения списка элементов
 */
export const List = forwardRef<HTMLUListElement, ListProps>(function List(props, ref) {
    const {
        size: propsSize,
        width = 'auto',
        children,
        className,
        'data-testid': dataTestId = 'List',
        onItemClick,
        pinnedSection,
        pinnedSectionAfter,
        showSeparator: showSeparatorProp,
        isVirtualList,
        search,
        searchEmptyText = i18n('Ничего не найдено'),
    } = props;
    const size = isMobile() ? 'm' : propsSize;
    const scrollClass = getScrollbarClassName();
    const sizeSkinClass = getSizeSkin(size);
    const { LogProvider } = useLogger('List', props.logNode);
    const { onItemClick: contextOnItemClick } = useContext(MenuListContext);

    const contextValue = useMemo(
        () => ({ size, onItemClick: chain(contextOnItemClick, onItemClick) }),
        [size, contextOnItemClick, onItemClick],
    );
    const isStrictWidth = typeof width === 'number';
    const style = isStrictWidth ? { width: `${width}px` } : undefined;
    const colorSkinClass = getColorSkin('gray-neutral', {});
    const query = search?.trim() ?? '';
    const searchContextValue = useMemo(() => ({ query, isActive: Boolean(query) }), [query]);
    const cssMods = {
        size,
        width: isStrictWidth ? undefined : width,
        searching: searchContextValue.isActive,
    };

    // По умолчанию разделитель нужен, только если у списка есть основной контент
    const showSeparator = showSeparatorProp ?? Boolean(children);

    const stackClass = useVStack({});

    const content = isVirtualList ? (
        children
    ) : (
        <VStack
            scrollable
            maxHeight={props.maxHeight !== undefined ? `${props.maxHeight}px` : undefined}
            className={cls('scrollable-content')}
        >
            <ListSearchContext.Provider value={searchContextValue}>{children}</ListSearchContext.Provider>
            {searchContextValue.isActive ? (
                <ListItem isDisabled className={cls('search-empty')} data-testid={`${dataTestId}.SearchEmpty`}>
                    {searchEmptyText}
                </ListItem>
            ) : null}
        </VStack>
    );

    return (
        <LogProvider>
            <ul
                className={cls(cssMods, [scrollClass, sizeSkinClass, className, colorSkinClass, stackClass])}
                style={style}
                data-testid={dataTestId}
                ref={ref}
            >
                <ListContext.Provider value={contextValue}>
                    {pinnedSection ? (
                        <>
                            <ListSection>{pinnedSection}</ListSection>
                            {showSeparator && <ListSection />}
                        </>
                    ) : null}
                    {content}
                    {pinnedSectionAfter ? (
                        <>
                            {showSeparator && !pinnedSection ? <ListSection /> : null}
                            <ListSection>{pinnedSectionAfter}</ListSection>
                        </>
                    ) : null}
                </ListContext.Provider>
            </ul>
        </LogProvider>
    );
});
